import { useEffect, useRef, useState } from 'react';
import { IconEraser, IconPenLine } from '../icons';
import { formatDate } from '../i18n';
import { useTranslate } from '../i18n/react';
import { Button } from '../ui/Button';
import { Field, TextInput } from '../ui/Field';

/**
 * Somebody at the store signing for the visit.
 *
 * <p><b>Pointer events, not touch events.</b> A finger, a stylus and a mouse all arrive the
 * same way, so the drawing works on the auditor's phone and on the web screen a store
 * manager might use — which the analysis of the system this replaces lists as a requirement
 * rather than a nicety.</p>
 *
 * <p>The typed name is not decoration either. A squiggle proves very little: nobody can read
 * it, and nothing about it says who drew it. What makes the pair worth keeping is that the
 * name is readable and the record it sits in already has its questions and its score fixed
 * by a hash.</p>
 *
 * <p>The pad is white in both themes and the ink is dark in both. What is stored is a
 * transparent PNG that will be shown on white in a report, so a pad that followed the dark
 * theme would have the signer drawing in ink they cannot see.</p>
 */
export function SignaturePad({
  onSign,
  onClear,
  signed,
  busy,
}: {
  onSign: (png: Blob, name: string) => void;
  onClear: () => void;
  signed: { signedByName: string; signedAt: string; url: string | null } | null;
  busy: boolean;
}) {
  const t = useTranslate();
  const canvas = useRef<HTMLCanvasElement>(null);
  const drawing = useRef(false);
  const [drawn, setDrawn] = useState(false);
  const [name, setName] = useState('');

  // The canvas is sized in device pixels and scaled back down, so a line drawn on a phone
  // is a line rather than a staircase.
  useEffect(() => {
    const element = canvas.current;
    if (!element || signed) return;

    const ratio = window.devicePixelRatio || 1;
    const width = element.clientWidth;
    const height = element.clientHeight;

    element.width = width * ratio;
    element.height = height * ratio;

    const context = element.getContext('2d');
    if (!context) return;

    context.scale(ratio, ratio);
    context.lineWidth = 2.5;
    context.lineCap = 'round';
    context.lineJoin = 'round';
    context.strokeStyle = '#111111';
  }, [signed]);

  if (signed) {
    return (
      <div className="nv-signature">
        {signed.url ? (
          <img className="nv-signature-preview" src={signed.url} alt={signed.signedByName} />
        ) : (
          <span className="nv-hint">{t('audit.photoUnavailable')}</span>
        )}

        <div className="nv-signature__who">
          <strong>{signed.signedByName}</strong>
          <span className="nv-hint">{formatDate(signed.signedAt, { dateStyle: 'medium', timeStyle: 'short' })}</span>
        </div>

        <Button icon={IconPenLine} disabled={busy} onClick={onClear}>{t('audit.signAgain')}</Button>
      </div>
    );
  }

  const point = (event: React.PointerEvent<HTMLCanvasElement>) => {
    const box = event.currentTarget.getBoundingClientRect();
    return { x: event.clientX - box.left, y: event.clientY - box.top };
  };

  const begin = (event: React.PointerEvent<HTMLCanvasElement>) => {
    const context = canvas.current?.getContext('2d');
    if (!context) return;

    // Captured, so a finger that leaves the box mid-stroke keeps drawing instead of
    // stopping where the edge is.
    event.currentTarget.setPointerCapture(event.pointerId);
    drawing.current = true;

    const { x, y } = point(event);
    context.beginPath();
    context.moveTo(x, y);
  };

  const extend = (event: React.PointerEvent<HTMLCanvasElement>) => {
    if (!drawing.current) return;

    const context = canvas.current?.getContext('2d');
    if (!context) return;

    const { x, y } = point(event);
    context.lineTo(x, y);
    context.stroke();
    setDrawn(true);
  };

  const end = () => {
    drawing.current = false;
  };

  const wipe = () => {
    const element = canvas.current;
    const context = element?.getContext('2d');
    if (!element || !context) return;

    context.clearRect(0, 0, element.width, element.height);
    setDrawn(false);
  };

  const take = () => {
    const element = canvas.current;
    if (!element || !drawn || !name.trim()) return;

    element.toBlob((blob) => {
      if (blob) onSign(blob, name.trim());
    }, 'image/png');
  };

  return (
    <div className="nv-stack">
      <canvas
        className="nv-signpad"
        ref={canvas}
        aria-label={t('audit.sign')}
        onPointerDown={begin}
        onPointerMove={extend}
        onPointerUp={end}
        onPointerCancel={end}
      />

      <Field label={t('audit.signedBy')} hint={t('audit.signedByHint')}>
        {({ id, describedBy }) => (
          <TextInput
            id={id}
            aria-describedby={describedBy}
            value={name}
            onChange={(e) => setName(e.target.value)}
          />
        )}
      </Field>

      <div className="nv-row">
        <Button
          variant="primary"
          icon={IconPenLine}
          disabled={!drawn || !name.trim() || busy}
          onClick={take}
        >
          {t('audit.sign')}
        </Button>
        <Button variant="ghost" icon={IconEraser} disabled={!drawn} onClick={wipe}>
          {t('audit.signClear')}
        </Button>
      </div>
    </div>
  );
}
